import { useModal } from './Modal';
import { getGameColor } from '../data/games';
import HeartIcon from './HeartIcon';
import DismissIcon from './DismissIcon';

// A single game row, divider-separated, for use inside a block (Browse,
// or the "More to Play" list on Home).
function GameListing({ isFavorite, toggleFavorite, onGameLinkClick, game, dimGame, onUnlike }) {
  const { showModal } = useModal();
  const accentColor = getGameColor(game);

  const onClickFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isFavorite) toggleFavorite(game.id, false);
    else toggleFavorite(game.id, true);
  }

  const onClickUnlike = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onUnlike(game.id);
  }

  const handleIconClick = (e, msg, iconTitle) => {
    e.preventDefault();
    e.stopPropagation();
    showModal(msg, iconTitle);
  };

  let icons = [];
  if (game.isLinked) {
    icons.push(
        <img key="linked" src="/img/plug.png" alt="linked game" className="w-6 h-6 inline-block ml-1.5 -mt-1 cursor-pointer" onClick={(e) => handleIconClick(e, 'This game will automatically log your score to your NiceLight.Games account.', 'Auto-Scoring')} />
    );
  }
  if (game.isNew) {
    icons.push(
        <img key="new" src="/img/new.png" alt="new game" className="w-6 h-6 inline-block ml-1.5 -mt-1 cursor-pointer" onClick={(e) => handleIconClick(e, 'This game was recently added to Nice Light Games.', 'New Game')}/>
    );
  }
  if (game.isLocked) {
    icons.push(
        <img key="locked" src="/img/lock.png" alt="locked game" className="w-6 h-6 inline-block ml-1.5 -mt-1 cursor-pointer"  onClick={(e) => handleIconClick(e, 'This game requires an account to play.', 'Login Required')}/>
    );
  }

  // Heart sits beside the X when there is one. Offsets are for the 38x44px hit
  // area, not the 28px circle inside it - see the buttons below.
  const heartPositionClass = onUnlike ? '-top-0.5 right-[39px]' : '-top-0.5 right-px';

  return (
    <li className={`relative list-none flex items-start gap-3 px-2 py-2 border-b border-nlg-rule last:border-none hover:bg-white/[0.04] transition-colors duration-150 ${dimGame ? 'bg-white/5' : ''}`}>
      <a
        href={game.url}
        className="flex items-start gap-3 flex-1 min-w-0"
        onClick={(e) => {
          e.preventDefault();
          onGameLinkClick(game);
        }}
      >
        <img
          src={"/img/games/" + game.image}
          className="w-[50px] h-[50px] rounded-2xl border-2 border-black flex-shrink-0"
          alt={game.name}
        />
        <div className={`min-w-0 flex-1 text-left ${dimGame ? 'opacity-50' : ''}`}>
          <p className={`font-display font-bold text-lg sm:text-xl leading-tight ${onUnlike ? 'pr-16' : 'pr-8'}`} style={{ color: accentColor }}>
            {game.name}{icons}
          </p>
          <p className="text-sm text-nlg-muted leading-snug mt-1">{game.caption}</p>
        </div>
      </a>

      {/* Each button is a 38x44px transparent target around a 28px visible
          circle (the circle is the inner span), so a slightly-off tap lands
          on the button instead of falling through to the game link. 38px is
          the circles' centre-to-centre spacing, so the two targets tile
          without overlapping, and the offsets keep the circles exactly where
          they were (centres 20px and 58px from the right edge, 20px down). */}
      {onUnlike && (
        <button
          onClick={onClickUnlike}
          className="group absolute -top-0.5 right-px w-[38px] h-11 flex items-center justify-center"
          aria-label="Not interested, remove from Editor's Picks"
          title="Not interested"
        >
          <span
            className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 group-hover:scale-110"
            style={{ borderColor: accentColor, borderWidth: 1, borderStyle: 'solid' }}
          >
            <DismissIcon color={accentColor} className="w-[16.5px] h-[16.5px]" />
          </span>
        </button>
      )}

      <button
        onClick={onClickFavorite}
        className={`group absolute ${heartPositionClass} w-[38px] h-11 flex items-center justify-center`}
        aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
      >
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 group-hover:scale-110"
          style={{
            borderColor: accentColor,
            borderWidth: 1,
            borderStyle: 'solid',
            ...(isFavorite ? { backgroundColor: `color-mix(in srgb, ${accentColor} 18%, transparent)` } : {})
          }}
        >
          <HeartIcon
            filled={isFavorite}
            color={accentColor}
            className="w-[13.5px] h-[13.5px]"
          />
        </span>
      </button>
    </li>
  );
}

export default GameListing;
